import { useCallback } from "react";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { twMerge } from "tailwind-merge";
import { useAtomCallback } from "jotai/utils";
import { IconButton, Tooltip } from "@growtherapy/sprout-ui";

import speakerTestChimeUri from "../assets/audio/speaker-test-chime.mp3?url";
import { usePlayAudioCallback } from "../hooks";
import { getSmoothingFunction } from "../session/useGetInputVolume";
import { VerticalDivider } from "./VerticalDivider";
import { VolumeMonitor } from "./VolumeMonitor";
import classNames from "classnames";
import { currentOutputDeviceIdAtom } from "../video-sdk/core/atoms";

const VOLUME_INDICATOR_COUNT = 8;
const volumeSmoothingFunction = getSmoothingFunction({
  maxVolume: VOLUME_INDICATOR_COUNT,
});

export function TestSpeakersButtonComponent({
  audioIsPlaying = false,
  onPlayTestAudio,
  outputVolume = 0,
}: {
  audioIsPlaying?: boolean;
  onPlayTestAudio: () => void;
  outputVolume?: number;
}) {
  // TODO: expose getButtonStyling from sprout-ui
  // const buttonClassName = getButtonStyling();
  const buttonClassName =
    "relative ml-2 transition first-of-type:ml-0 font-medium tracking-normal text-neutral-800 focus-visible:outline-neutral-800 bg-neutral-300 shadow-border-rebrand-neutral-800 rounded hover:shadow-border-lilac-700 hover:bg-neutral-400 active:bg-neutral-500 active:text-neutral-700 active:shadow-border-green-700 focus-visible:shadow-border-lilac-700 focus-visible:outline-2 focus-visible:outline-offset-4 sprout-icon-button flex items-center px-4 py-4 sprout-button";

  return (
    <div
      className={twMerge(
        buttonClassName,
        "flex flex-row items-center m-0 p-0 pr-2",
      )}
    >
      <Tooltip
        childIsInteractive={!audioIsPlaying}
        text={audioIsPlaying ? "Speaker test running..." : "Test speakers"}
        className="z-0"
      >
        <IconButton
          aria-label="Test speakers"
          className={classNames("m-0", { "bg-transparent": audioIsPlaying })}
          iconDefinition={faPlay}
          onClick={onPlayTestAudio}
          disabled={audioIsPlaying}
          use="unstyled"
        />
      </Tooltip>

      <VerticalDivider className="mr-3 h-6" />

      <VolumeMonitor
        className="gap-[2px] mr-1"
        levelIndicatorClassName="w-2 sm:w-2 m-0"
        levelIndicatorCount={VOLUME_INDICATOR_COUNT}
        volume={outputVolume}
      />
    </div>
  );
}

export function TestSpeakersButton() {
  const getSelectedOutputDevice = useAtomCallback(
    useCallback((get) => get(currentOutputDeviceIdAtom), []),
  );

  const { playAudio, volume, isPlaying } = usePlayAudioCallback(
    speakerTestChimeUri,
    { smoothingFunction: volumeSmoothingFunction },
  );
  const onPlayTestAudio = useCallback(
    () => playAudio(getSelectedOutputDevice()),
    [playAudio, getSelectedOutputDevice],
  );

  return (
    <TestSpeakersButtonComponent
      audioIsPlaying={isPlaying}
      outputVolume={volume}
      onPlayTestAudio={onPlayTestAudio}
    />
  );
}
